import React, { useMemo } from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './StudentLevelProgressionChar.module.scss';
import { StudentMetricsDashboardLevelTransitions } from '@/school-management/types/generate-report';
import {
  normalizeStudentMetricsDashboardLevelTransitionsData,
  levelIds,
  levelColors,
} from '../normalizeData';

interface StudentLevelProgressionCharProps {
  data?: StudentMetricsDashboardLevelTransitions[];
  onSegmentSelect?: (currentLevel: number, startingLevel: number) => void;
}

const StudentLevelProgressionChar: React.FC<StudentLevelProgressionCharProps> = ({
  data,
  onSegmentSelect,
}) => {
  const { t } = useAdminTranslation();

  const levels = useMemo(() => {
    const levelsData = normalizeStudentMetricsDashboardLevelTransitionsData(data);

    return levelsData;
  }, [data]);

  return (
    <div className={styles.chart}>
      <div className={styles.levels}>
        {levels.map(item => {
          return (
            <div key={item.level} className={styles.levelBar}>
              <div className={styles.levelInfo}>
                {t('studentMetricsDashboard.charts.studentLevelProgression.levelInfo', {
                  level: item.level,
                  studentCount: item.studentCount,
                })}
              </div>
              <div className={styles.progressBarContainer}>
                <div className={styles.progressBarBackground}>
                  {item.parts.map(part => {
                    const progress = Math.min(Math.max(part.percentage, 0), 100);

                    return (
                      <div
                        key={`${item.level}-${part.startingLevel}`}
                        className={classes(styles.progressBarFill, {
                          [styles[part.color]]: !!part.color,
                        })}
                        style={{
                          width: `${progress}%`,
                          cursor: part.studentCount > 0 ? 'pointer' : 'default',
                        }}
                        onClick={() => {
                          if (part.studentCount > 0) {
                            onSegmentSelect?.(item.level, part.startingLevel);
                          }
                        }}
                      >
                        {progress > 0 && (
                          <span className={styles.progressValue}>
                            {part.studentCount} ({progress}%)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.tools}>
        {levelIds.map(level => {
          return (
            <div key={`StudentLevelProgressionChar-tool--${level}`}>
              <span className={styles[levelColors[level - 1]]}></span>
              <span>
                {t(
                  `studentMetricsDashboard.charts.studentLevelProgression.tools.studentStartedAtL${level}`
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StudentLevelProgressionChar;
